import { cn } from "@/lib/cn";
import type { ReactNode } from "react";

/** Focus ring an indicator draws when its visually hidden input (the `peer`) has keyboard focus. */
export const PEER_FOCUS_RING =
  "peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-2";

/** Ids of a choice control's label and description spans. */
export type ChoiceIds = { label: string; description: string };

/** Derives the label/description ids from one `useId()` value. */
export function choiceIds(base: string): ChoiceIds {
  return { label: `${base}-label`, description: `${base}-description` };
}

/** `aria-labelledby` / `aria-describedby` the hidden input needs so the description is not part of its name. */
export function choiceAria(
  ids: ChoiceIds,
  label: unknown,
  description: unknown,
  own: { "aria-labelledby"?: string; "aria-describedby"?: string },
) {
  return {
    "aria-labelledby": label ? ids.label : own["aria-labelledby"],
    "aria-describedby":
      [description ? ids.description : null, own["aria-describedby"]].filter(Boolean).join(" ") || undefined,
  };
}

/** Visually hidden native input that stays focusable and announced. */
export const HIDDEN_INPUT = "peer sr-only";

export type ChoiceRowProps = {
  input: ReactNode;
  indicator: ReactNode;
  label?: ReactNode;
  description?: ReactNode;
  /** From `choiceIds()`: links the label and description spans to the input. */
  ids: ChoiceIds;
  size: "sm" | "md";
  disabled?: boolean;
  /** Where the indicator sits relative to the text. */
  indicatorPosition?: "start" | "end";
  className?: string;
};

/** Shared `<label>` layout of Checkbox, Radio and Switch: hidden input, indicator, label and description. */
export function ChoiceRow({
  input,
  indicator,
  label,
  description,
  ids,
  size,
  disabled = false,
  indicatorPosition = "start",
  className,
}: ChoiceRowProps) {
  const hasText = Boolean(label || description);
  return (
    <label
      className={cn(
        "relative inline-flex items-start gap-3 text-card-text",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        indicatorPosition === "end" && "justify-between",
        className,
      )}
    >
      {input}
      {indicatorPosition === "start" ? indicator : null}
      {hasText ? (
        <span className={cn("flex min-w-0 flex-col gap-0.5", disabled && "opacity-50")}>
          {label ? (
            <span id={ids.label} className={size === "sm" ? "text-sm leading-5" : "text-base leading-6"}>
              {label}
            </span>
          ) : null}
          {description ? (
            <span id={ids.description} className="text-xs text-card-text/50">
              {description}
            </span>
          ) : null}
        </span>
      ) : null}
      {indicatorPosition === "end" ? indicator : null}
    </label>
  );
}
